/* eslint-disable camelcase */
import React from 'react'
import { Chip, Box, Tooltip, Typography } from '@material-ui/core'

export const ALL_SUBORGS_KEY = 'ALL_SUBORGS'
export const ALL_SUBORGS_LABEL = 'All Teams'

// Retained for backward-compatibility aliases
export const ALL_ORGS_KEY = 'ALL_SUBORGS'
export const ALL_ORGS_LABEL = 'All Teams'

export const WORKFLOW_FIELDS_MAIN = [
  'title',
  'nextLeadStatus',
  'tagsToAdd',
  'tagsToRemove',
  'fieldsToUpdate',
  'fieldsToClear',
]

export const WORKFLOW_FIELDS_ADVANCED = [
  'enabledSubOrgIds',
  'captureNote',
  'paymentTransaction',
  'workflowKey',
]

/**
 * Validates workflow title.
 * Allow ONLY uppercase letters, digits, spaces, parentheses, and underscores: /^[A-Z0-9_ ()]+$/
 */
export function isValidWorkflowTitle(title) {
  if (typeof title !== 'string') return false
  const trimmed = title.trim()
  if (!trimmed) return false
  return /^[A-Z0-9_ ()]+$/.test(trimmed)
}

/**
 * Sanitizes workflow title input by uppercasing and stripping disallowed characters.
 */
export function sanitizeWorkflowTitle(raw) {
  if (raw == null) return ''
  return String(raw).toUpperCase().replace(/[^A-Z0-9_ ()]/g, '')
}

/**
 * Validates workflowKey if provided.
 * Stable identifier (alphanumeric/underscore, must start with letter).
 * Can be blank/empty (backend auto-generates camelCase from title).
 */
export function isValidWorkflowKey(key) {
  if (key == null) return true
  const trimmed = String(key).trim()
  if (!trimmed) return true
  return /^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmed)
}

/**
 * Checks if workflow has at least one configured action.
 * Cannot enable a workflow (state: "LIVE") if it has no actions configured.
 * Must set at least one of:
 * nextLeadStatus, tagsToAdd, tagsToRemove, fieldsToUpdate, fieldsToClear,
 * waTemplateKey, captureNote === "ENABLED", or paymentTransaction === "ENABLED".
 */
export function hasConfiguredActions(workflow) {
  if (!workflow || typeof workflow !== 'object') return false

  if (workflow.nextLeadStatus && String(workflow.nextLeadStatus).trim() !== '' && workflow.nextLeadStatus !== 'NONE') {
    return true
  }
  if (Array.isArray(workflow.tagsToAdd) && workflow.tagsToAdd.length > 0) {
    return true
  }
  if (Array.isArray(workflow.tagsToRemove) && workflow.tagsToRemove.length > 0) {
    return true
  }
  if (Array.isArray(workflow.fieldsToUpdate) && workflow.fieldsToUpdate.length > 0) {
    return true
  }
  if (Array.isArray(workflow.fieldsToClear) && workflow.fieldsToClear.length > 0) {
    return true
  }
  if (workflow.waTemplateKey && String(workflow.waTemplateKey).trim() !== '' && workflow.waTemplateKey !== 'NONE') {
    return true
  }
  if (workflow.captureNote === 'ENABLED') {
    return true
  }
  if (workflow.paymentTransaction === 'ENABLED') {
    return true
  }

  return false
}

/**
 * True when list/detail API flagged the workflow's waTemplateKey as broken
 * (deleted from available keys, or unmapped / orphaned on the primary WA number).
 * Healthy keys omit _waKeyBroken entirely (not set to false).
 */
export function isWaKeyBroken(workflow) {
  return Boolean(workflow && workflow._waKeyBroken === true)
}

/**
 * Generates short chips summarizing what the workflow does.
 * e.g. "WA Message", "Status -> CONNECTED", "+Tag: Followup", "Note", etc.
 * When _waKeyBroken is set, the WA chip is marked broken with a tooltip reason.
 */
export function getActionChips(workflow) {
  if (!workflow || typeof workflow !== 'object') return []
  const chips = []

  if (workflow.waTemplateKey && workflow.waTemplateKey !== 'NONE') {
    const waBroken = isWaKeyBroken(workflow)
    const mappingHealth = getWaMappingHealth(workflow)
    const showWarning = waBroken || mappingHealth.broken
    chips.push({
      key: 'wa',
      label: 'WA Message',
      icon: 'whatsapp',
      color: showWarning ? 'warning' : 'default',
      variant: 'outlined',
      ...(showWarning ? {
        broken: true,
        tooltip: (waBroken
          ? (workflow._waKeyBrokenReason || 'WhatsApp template key is broken')
          : mappingHealth.reason)
          || 'WhatsApp template mapping needs attention',
      } : {}),
    })
  }

  if (workflow.nextLeadStatus && workflow.nextLeadStatus !== 'NONE') {
    chips.push({ key: 'status', label: `Status -> ${workflow.nextLeadStatus}`, color: 'default', variant: 'outlined' })
  }

  if (Array.isArray(workflow.tagsToAdd) && workflow.tagsToAdd.length > 0) {
    if (workflow.tagsToAdd.length <= 2) {
      workflow.tagsToAdd.forEach((tag, idx) => {
        chips.push({ key: `tag_add_${idx}`, label: `+Tag: ${tag}`, color: 'default', variant: 'outlined' })
      })
    } else {
      chips.push({ key: 'tags_add_multi', label: `+${workflow.tagsToAdd.length} Tags`, color: 'default', variant: 'outlined' })
    }
  }

  if (Array.isArray(workflow.tagsToRemove) && workflow.tagsToRemove.length > 0) {
    if (workflow.tagsToRemove.length <= 2) {
      workflow.tagsToRemove.forEach((tag, idx) => {
        chips.push({ key: `tag_rem_${idx}`, label: `-Tag: ${tag}`, color: 'default', variant: 'outlined' })
      })
    } else {
      chips.push({ key: 'tags_rem_multi', label: `-${workflow.tagsToRemove.length} Tags`, color: 'default', variant: 'outlined' })
    }
  }

  if (Array.isArray(workflow.fieldsToUpdate) && workflow.fieldsToUpdate.length > 0) {
    const statusOptionFields = workflow.fieldsToUpdate.filter(isStatusOptionField)
    const otherFields = workflow.fieldsToUpdate.filter(key => !isStatusOptionField(key))
    statusOptionFields.forEach((fieldKey, idx) => {
      chips.push({
        key: `status_option_${idx}`,
        label: formatFieldDisplayName(fieldKey),
        color: 'default',
        variant: 'outlined',
      })
    })
    if (otherFields.length > 0) {
      chips.push({
        key: 'update_fields',
        label: `Update Fields (${otherFields.length})`,
        color: 'default',
        variant: 'outlined',
      })
    }
  }

  if (Array.isArray(workflow.fieldsToClear) && workflow.fieldsToClear.length > 0) {
    chips.push({ key: 'clear_fields', label: `Clear Fields (${workflow.fieldsToClear.length})`, color: 'default', variant: 'outlined' })
  }

  if (workflow.captureNote === 'ENABLED') {
    chips.push({ key: 'note', label: 'Note', color: 'default', variant: 'outlined' })
  }

  if (workflow.paymentTransaction === 'ENABLED') {
    chips.push({ key: 'payment', label: 'Payment', color: 'default', variant: 'outlined' })
  }

  return chips
}

/**
 * Returns a human-friendly string summary of enabled teams.
 */
export function formatEnabledTeams(enabledSubOrgIds, teamOptions = []) {
  if (!Array.isArray(enabledSubOrgIds) || enabledSubOrgIds.length === 0) {
    return 'None'
  }

  const optionMap = {
    [ALL_SUBORGS_KEY]: ALL_SUBORGS_LABEL,
    ALL_ORGS: 'All Teams',
    MAIN_ORG: 'Main Org',
  }
  teamOptions.forEach(opt => {
    if (opt && opt.key != null) {
      optionMap[opt.key] = opt.label || opt.text || opt.key
    }
  })

  const names = enabledSubOrgIds.map(id => optionMap[id] || id)
  if (names.length <= 2) {
    return names.join(', ')
  }
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`
}

/**
 * Resolves main org id from subOrgOptions or falls back to orgId.
 */
export function getMainOrgId(subOrgOptions = [], orgId = '') {
  if (Array.isArray(subOrgOptions)) {
    const mainByLabel = subOrgOptions.find(o => {
      if (!o) return false
      const label = (o.label || o.text || '').trim().toLowerCase()
      return label === 'main org' || label.startsWith('main org') || label.includes('main org')
    })
    if (mainByLabel && (mainByLabel.key != null || mainByLabel.value != null)) {
      return String(mainByLabel.key != null ? mainByLabel.key : mainByLabel.value)
    }

    if (orgId && orgId !== 'PRODUCT') {
      const mainById = subOrgOptions.find(o => o && (String(o.key) === String(orgId) || String(o.value) === String(orgId)))
      if (mainById && (mainById.key != null || mainById.value != null)) {
        return String(mainById.key != null ? mainById.key : mainById.value)
      }
    }

    const mainByKey = subOrgOptions.find(o => o && (o.key === 'MAIN_ORG' || o.value === 'MAIN_ORG'))
    if (mainByKey && (mainByKey.key != null || mainByKey.value != null)) {
      return String(mainByKey.key != null ? mainByKey.key : mainByKey.value)
    }
  }

  if (orgId && orgId !== 'PRODUCT') {
    return String(orgId)
  }

  return ''
}

/**
 * Returns default enabledSubOrgIds selection: ALL_SUBORGS + Main Org.
 */
export function getDefaultEnabledSubOrgIds(subOrgOptions = [], orgId = '') {
  const allSubOrgsOpt = Array.isArray(subOrgOptions)
    ? subOrgOptions.find(o => o && (o.key === ALL_SUBORGS_KEY || o.key === 'ALL_ORGS' || ((o.label || o.text) && String(o.label || o.text).toLowerCase() === 'all teams')))
    : null
  const allTeamsKey = allSubOrgsOpt ? String(allSubOrgsOpt.key) : ALL_SUBORGS_KEY

  const result = [allTeamsKey]
  const mainOrgId = getMainOrgId(subOrgOptions, orgId)
  if (mainOrgId && !result.includes(mainOrgId)) {
    result.push(mainOrgId)
  }
  return result
}

/**
 * Returns subOrgOptions directly from fieldDef without injecting synthetic options.
 */
export function getSubOrgOptions(fieldDef) {
  const raw = getFieldOptions(fieldDef, 'enabledSubOrgIds')
  if (!Array.isArray(raw)) return []
  return raw
    .map(opt => {
      if (!opt) return null
      if (typeof opt === 'object') {
        return {
          key: opt.key != null ? opt.key : opt.value,
          label: opt.label || opt.text || opt.key || opt.value,
        }
      }
      return { key: String(opt), label: String(opt) }
    })
    .filter(Boolean)
}

/**
 * True when the tenant has at least one real team (excludes ALL_SUBORGS / ALL_ORGS).
 */
export function tenantHasWorkflowTeams(fieldDef, subOrgs = []) {
  const rawSubOrgOptions = getFieldOptions(fieldDef, 'enabledSubOrgIds')
  const actualTeams = (Array.isArray(rawSubOrgOptions) ? rawSubOrgOptions : []).filter((opt) => {
    const k = typeof opt === 'object' ? (opt.key || opt.value) : opt
    return k && k !== ALL_SUBORGS_KEY && k !== 'ALL_ORGS'
  })
  return actualTeams.length > 0 || (Array.isArray(subOrgs) && subOrgs.length > 0)
}

/**
 * Resolves next enabledSubOrgIds selection according to ALL_SUBORGS rules:
 * - ALL_SUBORGS represents all sub-teams (excluding Main Org).
 */
export function resolveSubOrgSelection(prevSelected = [], nextSelected = []) {
  const prevKeys = prevSelected.map(opt => (typeof opt === 'object' ? opt.key || opt.value : opt))
  const nextKeys = nextSelected.map(opt => (typeof opt === 'object' ? opt.key || opt.value : opt))

  const hadAll = prevKeys.includes(ALL_SUBORGS_KEY)
  const hasAll = nextKeys.includes(ALL_SUBORGS_KEY)

  if (!hadAll && hasAll) {
    return [ALL_SUBORGS_KEY]
  }

  if (hadAll && hasAll && nextKeys.length > 1) {
    return nextKeys.filter(k => k !== ALL_SUBORGS_KEY)
  }

  if (hadAll && !hasAll) {
    return nextKeys.filter(k => k !== ALL_SUBORGS_KEY)
  }

  return nextKeys.filter(k => k !== ALL_SUBORGS_KEY)
}

/**
 * Normalizes enabledSubOrgIds for payload submission:
 * - If ALL_SUBORGS is present, keeps ALL_SUBORGS along with mainOrgId (if present).
 * - Rewrites legacy sentinel MAIN_ORG / "Main Org" to the real parent org id.
 */
export function cleanEnabledSubOrgIds(enabledSubOrgIds, mainOrgId = '') {
  if (!Array.isArray(enabledSubOrgIds)) return []
  const mainId = mainOrgId ? String(mainOrgId) : ''
  const normalized = enabledSubOrgIds
    .map(id => (typeof id === 'object' && id ? (id.key != null ? id.key : (id.value != null ? id.value : id.label)) : id))
    .map(id => (id === 'ALL_ORGS' || id === 'All Teams' ? ALL_SUBORGS_KEY : id))
    .map(id => (id === 'MAIN_ORG' || id === 'Main Org' ? (mainId || null) : id))
    .filter(Boolean)
    .map(String)

  const unique = Array.from(new Set(normalized))
  const hasAllSubOrgs = unique.includes(ALL_SUBORGS_KEY)

  if (hasAllSubOrgs) {
    const result = [ALL_SUBORGS_KEY]
    if (mainId && unique.includes(mainId)) {
      result.push(mainId)
    }
    return result
  }

  return unique
}

/**
 * True when id belongs to this tenant: ALL_SUBORGS, main org, fieldDef option, or `{orgId}-*` team.
 * Rejects foreign org/team ids (e.g. leaked from another account's legacy contexts).
 */
export function isEnabledSubOrgIdForOrg(id, orgId = '', allowedKeys = null) {
  if (id == null || id === '') return false
  const s = String(id)
  if (s === ALL_SUBORGS_KEY || s === 'ALL_ORGS') return true
  const org = orgId ? String(orgId) : ''
  if (org && s === org) return true
  if (allowedKeys && allowedKeys.size > 0) {
    return allowedKeys.has(s)
  }
  if (org) {
    return s.startsWith(`${org}-`)
  }
  return false
}

/**
 * Drops enabledSubOrgIds that do not belong to the current org.
 * If nothing valid remains, falls back to [ALL_SUBORGS, mainOrg].
 */
export function sanitizeEnabledSubOrgIdsForOrg(enabledSubOrgIds, subOrgOptions = [], orgId = '') {
  const mainOrgId = getMainOrgId(subOrgOptions, orgId)
  const cleaned = cleanEnabledSubOrgIds(enabledSubOrgIds, mainOrgId)

  const optionKeys = new Set(
    (Array.isArray(subOrgOptions) ? subOrgOptions : [])
      .map((o) => (o && (o.key != null ? o.key : o.value)))
      .filter((k) => k != null && k !== '')
      .map(String)
  )
  if (mainOrgId) optionKeys.add(String(mainOrgId))
  if (orgId) optionKeys.add(String(orgId))
  optionKeys.add(ALL_SUBORGS_KEY)
  optionKeys.add('ALL_ORGS')

  const hasFieldOptions = (Array.isArray(subOrgOptions) ? subOrgOptions : []).some((o) => {
    const k = o && (o.key != null ? o.key : o.value)
    return k && k !== ALL_SUBORGS_KEY && k !== 'ALL_ORGS'
  })

  const filtered = cleaned.filter((id) => (
    isEnabledSubOrgIdForOrg(id, orgId, hasFieldOptions ? optionKeys : null)
  ))

  if (filtered.length === 0) {
    return getDefaultEnabledSubOrgIds(subOrgOptions, orgId)
  }
  return filtered
}

/**
 * Renders badges/chips for the "Enabled For" column in the workflows listing table.
 * - If contains ALL_SUBORGS / ALL_ORGS: chip/badge "All Teams".
 * - If specific teams selected: badges for selected teams or "+N Teams" if > 2.
 * - If empty: muted text "None".
 * All team chips use the same outlined style.
 */
export function renderEnabledForBadges(enabledSubOrgIds, subOrgOptions = []) {
  if (!Array.isArray(enabledSubOrgIds) || enabledSubOrgIds.length === 0) {
    return (
      <Typography variant='caption' color='textSecondary' style={{ fontStyle: 'italic' }}>
        None
      </Typography>
    )
  }

  const optionMap = {
    [ALL_SUBORGS_KEY]: ALL_SUBORGS_LABEL,
    ALL_ORGS: 'All Teams',
    MAIN_ORG: 'Main Org',
  }
  subOrgOptions.forEach(opt => {
    if (opt && opt.key != null) {
      optionMap[opt.key] = opt.label || opt.text || opt.key
    }
  })

  const chipStyle = { fontSize: 11, height: 22 }

  if (enabledSubOrgIds.length === 1 && (enabledSubOrgIds.includes(ALL_SUBORGS_KEY) || enabledSubOrgIds.includes('ALL_ORGS'))) {
    return (
      <Chip
        size='small'
        variant='outlined'
        label='All Teams'
        style={chipStyle}
      />
    )
  }

  if (enabledSubOrgIds.length <= 2) {
    return (
      <Box display='flex' flexWrap='wrap' style={{ gap: 4 }}>
        {enabledSubOrgIds.map(id => (
          <Chip
            key={id}
            size='small'
            variant='outlined'
            label={optionMap[id] || id}
            style={chipStyle}
          />
        ))}
      </Box>
    )
  }

  const firstId = enabledSubOrgIds[0]
  const remainingCount = enabledSubOrgIds.length - 1
  const allNames = enabledSubOrgIds.map(id => optionMap[id] || id)

  return (
    <Box display='flex' flexWrap='wrap' alignItems='center' style={{ gap: 4 }}>
      <Chip
        size='small'
        variant='outlined'
        label={optionMap[firstId] || firstId}
        style={chipStyle}
      />
      <Tooltip title={allNames.join(', ')} arrow placement='top'>
        <Chip
          size='small'
          variant='outlined'
          label={`+${remainingCount} Teams`}
          style={{ ...chipStyle, backgroundColor: '#F3F4F6', cursor: 'pointer' }}
        />
      </Tooltip>
    </Box>
  )
}

/**
 * Extracts variable placeholders from a Meta WhatsApp template structure.
 * Used for preview/fallback when server waComponents skeleton is not yet available.
 */
export function extractTemplatePlaceholders(waTemplate) {
  const placeholders = []
  if (!waTemplate || typeof waTemplate !== 'object') return placeholders

  const components = Array.isArray(waTemplate.components) ? waTemplate.components : []

  components.forEach(comp => {
    if (!comp || typeof comp !== 'object') return
    const type = String(comp.type || '').toUpperCase()
    const format = String(comp.format || '').toUpperCase()

    if (type === 'HEADER') {
      if (format === 'TEXT') {
        const text = String(comp.text || '')
        const matches = text.match(/\{\{(\d+)\}\}/g) || []
        const uniqueIndices = [...new Set(matches.map(m => parseInt(m.replace(/\D/g, ''), 10)))]
        uniqueIndices.sort((a, b) => a - b).forEach(idx => {
          placeholders.push({
            id: `HEADER_${idx}`,
            type: 'header',
            format: 'text',
            vIndex: idx,
            label: `Header variable {{${idx}}}`,
            example: (comp.example && comp.example.header_text && comp.example.header_text[idx - 1]) || '',
          })
        })
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
        placeholders.push({
          id: 'HEADER_MEDIA',
          type: 'header',
          format: format.toLowerCase(),
          vIndex: 1,
          label: `Header ${format.toLowerCase()}`,
          isMedia: true,
          example: (comp.example && comp.example.header_handle && comp.example.header_handle[0]) || '',
        })
      }
    } else if (type === 'BODY') {
      const text = String(comp.text || '')
      const matches = text.match(/\{\{(\d+)\}\}/g) || []
      const uniqueIndices = [...new Set(matches.map(m => parseInt(m.replace(/\D/g, ''), 10)))]
      uniqueIndices.sort((a, b) => a - b).forEach(idx => {
        const exampleRow = comp.example && Array.isArray(comp.example.body_text) && comp.example.body_text[0]
        placeholders.push({
          id: `BODY_${idx}`,
          type: 'body',
          format: 'text',
          vIndex: idx,
          label: `Body variable {{${idx}}}`,
          example: (Array.isArray(exampleRow) && exampleRow[idx - 1]) || '',
        })
      })
    } else if (type === 'BUTTONS') {
      const buttons = Array.isArray(comp.buttons) ? comp.buttons : []
      buttons.forEach((btn, bIdx) => {
        if (!btn || typeof btn !== 'object') return
        const btnType = String(btn.type || '').toUpperCase()
        if (btnType === 'URL' || btnType === 'FLOW') {
          const url = String(btn.url || '')
          const matches = btnType === 'URL' ? (url.match(/\{\{(\d+)\}\}/g) || []) : ['{{1}}']
          const uniqueIndices = [...new Set(matches.map(m => parseInt(m.replace(/\D/g, ''), 10)))]
          uniqueIndices.sort((a, b) => a - b).forEach(idx => {
            placeholders.push({
              id: `BUTTON_${bIdx}_${idx}`,
              type: 'buttons',
              subType: btn.type,
              format: 'text',
              vIndex: idx,
              buttonIndex: bIdx,
              label: `Button "${btn.text || bIdx + 1}" ${btnType} variable {{${idx}}}`,
              example: (Array.isArray(btn.example) && btn.example[idx - 1]) || '',
            })
          })
        }
      })
    }
  })

  return placeholders
}

/**
 * Converts `waComponents` from either dictionary or array into an indexed map:
 * id -> component object. Preserves BE sequential ids ("1","2",...).
 */
export function normalizeWaComponentsMap(waComponents) {
  const map = {}
  if (!waComponents) return map

  if (!Array.isArray(waComponents) && typeof waComponents === 'object') {
    Object.keys(waComponents).forEach((key) => {
      const item = waComponents[key]
      if (!item || typeof item !== 'object') return
      const id = String(item.id != null ? item.id : key)
      map[id] = { ...item, id }
    })
    return map
  }

  if (Array.isArray(waComponents)) {
    waComponents.forEach((item, index) => {
      if (!item || typeof item !== 'object') return
      const id = String(item.id != null ? item.id : (index + 1))
      map[id] = { ...item, id }
    })
  }

  return map
}

function formatWaSlotLabel(slot) {
  if (!slot) return 'Variable'
  const type = String(slot.type || '').toLowerCase()
  const format = String(slot.format || 'text').toLowerCase()
  const vIndex = slot.vIndex
  if (type === 'header' && ['image', 'video', 'document'].includes(format)) {
    return `Header ${format}`
  }
  if (type === 'header') {
    return `Header variable {{${vIndex || 1}}}`
  }
  if (type === 'body') {
    return `Body variable {{${vIndex || 1}}}`
  }
  if (type === 'buttons') {
    const sub = slot.subType || 'URL'
    const btn = slot.buttonIndex != null ? Number(slot.buttonIndex) + 1 : ''
    return `Button${btn ? ` ${btn}` : ''} ${sub} variable {{${vIndex || 1}}}`
  }
  return slot.label || `Variable {{${vIndex || 1}}}`
}

/**
 * Mapping slots for the Configure WhatsApp dialog.
 * Prefers server waComponents skeleton (sequential ids); falls back to Meta template placeholders.
 */
export function getWaMappingSlots(waComponents, waTemplate) {
  const normalized = normalizeWaComponentsMap(waComponents)
  const keys = Object.keys(normalized)
  if (keys.length > 0) {
    // Keep numeric-ish ids sorted numerically when possible
    keys.sort((a, b) => {
      const na = Number(a)
      const nb = Number(b)
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb
      return String(a).localeCompare(String(b))
    })
    return keys.map((id) => {
      const slot = normalized[id]
      const format = String(slot.format || 'text').toLowerCase()
      const isMedia = ['image', 'video', 'document'].includes(format)
      return {
        ...slot,
        id: String(slot.id || id),
        type: String(slot.type || '').toLowerCase(),
        format,
        isMedia,
        label: formatWaSlotLabel(slot),
      }
    })
  }
  return extractTemplatePlaceholders(waTemplate).map((ph) => ({
    ...ph,
    isMedia: Boolean(ph.isMedia),
    label: ph.label || formatWaSlotLabel(ph),
  }))
}

/**
 * True when every mapping slot has a non-empty value
 * (text for variables, mediaLink/media for media headers).
 */
export function isWaMappingSlotFilled(slot, waComponents) {
  if (!slot || slot.id == null) return false
  const bound = (waComponents && waComponents[slot.id]) || slot || {}
  if (slot.isMedia || ['image', 'video', 'document'].includes(String(slot.format || '').toLowerCase())) {
    return Boolean(String(bound.mediaLink || bound.media || '').trim())
  }
  return Boolean(String(bound.text || '').trim())
}

export function areAllWaMappingSlotsFilled(mappingSlots, waComponents) {
  if (!Array.isArray(mappingSlots) || mappingSlots.length === 0) return true
  return mappingSlots.every((slot) => isWaMappingSlotFilled(slot, waComponents))
}

function sortedWaComponentIds(map) {
  return Object.keys(map || {}).sort((a, b) => {
    const na = Number(a)
    const nb = Number(b)
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb
    return String(a).localeCompare(String(b))
  })
}

function waSlotFormat(slot) {
  return String((slot && slot.format) || 'text').toLowerCase()
}

function waSlotType(slot) {
  return String((slot && slot.type) || '').toLowerCase()
}

function isCompatibleWaBinding(expectedSlot, previous) {
  if (!expectedSlot || !previous) return false
  if (waSlotFormat(expectedSlot) !== waSlotFormat(previous)) return false
  const expType = waSlotType(expectedSlot)
  const prevType = waSlotType(previous)
  if (expType && prevType && expType !== prevType) return false
  return true
}

function emptyWaSlotValues(format) {
  const isMedia = ['image', 'video', 'document'].includes(String(format || '').toLowerCase())
  return {
    text: '',
    media: '',
    mediaLink: '',
    ...(String(format || '').toLowerCase() === 'document' ? { fileName: '' } : {}),
    ...(isMedia ? {} : {}),
  }
}

/**
 * Stable fingerprint of Meta template structure/content.
 * Used to detect when a waTemplateKey now resolves to a different template.
 */
export function getWaTemplateFingerprint(waTemplate) {
  if (!waTemplate || typeof waTemplate !== 'object') return ''
  const components = Array.isArray(waTemplate.components) ? waTemplate.components : []
  return components.map((comp) => {
    if (!comp || typeof comp !== 'object') return ''
    const type = String(comp.type || '').toUpperCase()
    const format = String(comp.format || '').toUpperCase()
    const text = String(comp.text || '')
    const buttons = Array.isArray(comp.buttons)
      ? comp.buttons.map((b) => `${String((b && b.type) || '')}:${String((b && (b.url || b.text)) || '')}`).join(',')
      : ''
    return `${type}|${format}|${text}|${buttons}`
  }).join('||')
}

/**
 * True when live Meta template variable structure no longer matches stored
 * bindings (slot count or type/format changed). Content-only template edits
 * do not trigger a reset — previous mappings are preserved via remap.
 */
export function shouldResetWaVariableBindings(storedTemplate, liveTemplate, storedComponents) {
  if (!liveTemplate || typeof liveTemplate !== 'object') return false
  const liveSlots = extractTemplatePlaceholders(liveTemplate)
  if (liveSlots.length === 0) return false

  const stored = normalizeWaComponentsMap(storedComponents)
  const storedIds = sortedWaComponentIds(stored)
  if (storedIds.length === 0) return false
  if (storedIds.length !== liveSlots.length) return true
  for (let i = 0; i < liveSlots.length; i += 1) {
    const storedSlot = stored[storedIds[i]]
    const expected = liveSlots[i]
    if (waSlotFormat(storedSlot) !== waSlotFormat(expected)) return true
    if (waSlotType(expected) && waSlotType(storedSlot) && waSlotType(expected) !== waSlotType(storedSlot)) {
      return true
    }
  }
  return false
}

/**
 * Rebuild waComponents from the Meta template (+ optional BE skeleton ids).
 * Carries previous bindings only when type+format still match (image must not
 * stick on a video header after a template-key switch).
 */
export function remapWaComponentsToTemplate(waTemplate, previousComponents, beSkeleton) {
  const previous = normalizeWaComponentsMap(previousComponents)
  const skeletonSource = normalizeWaComponentsMap(
    beSkeleton != null ? beSkeleton : previousComponents
  )
  const templateSlots = extractTemplatePlaceholders(waTemplate)
  const prevIds = sortedWaComponentIds(previous)
  const prevList = prevIds.map((id) => previous[id])
  const usedPrev = new Set()

  const baseSlots = []
  const skeletonIds = sortedWaComponentIds(skeletonSource)

  if (skeletonIds.length > 0) {
    skeletonIds.forEach((id, idx) => {
      const be = skeletonSource[id] || {}
      const expected = templateSlots[idx]
      if (expected) {
        baseSlots.push({
          id: String(id),
          type: waSlotType(expected),
          format: waSlotFormat(expected),
          vIndex: expected.vIndex != null ? expected.vIndex : be.vIndex,
          ...(expected.subType || be.subType ? { subType: expected.subType || be.subType } : {}),
          ...(expected.buttonIndex != null || be.buttonIndex != null
            ? { buttonIndex: expected.buttonIndex != null ? expected.buttonIndex : be.buttonIndex }
            : {}),
        })
      } else {
        baseSlots.push({
          id: String(id),
          type: waSlotType(be),
          format: waSlotFormat(be),
          ...(be.vIndex != null ? { vIndex: be.vIndex } : {}),
          ...(be.subType ? { subType: be.subType } : {}),
          ...(be.buttonIndex != null ? { buttonIndex: be.buttonIndex } : {}),
        })
      }
    })
    templateSlots.slice(skeletonIds.length).forEach((ph) => {
      baseSlots.push({
        id: String(ph.id),
        type: waSlotType(ph),
        format: waSlotFormat(ph),
        ...(ph.vIndex != null ? { vIndex: ph.vIndex } : {}),
        ...(ph.subType ? { subType: ph.subType } : {}),
        ...(ph.buttonIndex != null ? { buttonIndex: ph.buttonIndex } : {}),
      })
    })
  } else {
    templateSlots.forEach((ph) => {
      baseSlots.push({
        id: String(ph.id),
        type: waSlotType(ph),
        format: waSlotFormat(ph),
        ...(ph.vIndex != null ? { vIndex: ph.vIndex } : {}),
        ...(ph.subType ? { subType: ph.subType } : {}),
        ...(ph.buttonIndex != null ? { buttonIndex: ph.buttonIndex } : {}),
      })
    })
  }

  const result = {}
  baseSlots.forEach((slot, idx) => {
    const id = String(slot.id)
    const format = waSlotFormat(slot)
    const isMedia = ['image', 'video', 'document'].includes(format)

    let prev = null
    if (previous[id] && isCompatibleWaBinding(slot, previous[id])) {
      prev = previous[id]
    } else if (prevList[idx] && isCompatibleWaBinding(slot, prevList[idx])) {
      prev = prevList[idx]
    } else {
      prev = prevList.find((p) => {
        if (!p || usedPrev.has(String(p.id))) return false
        if (!isCompatibleWaBinding(slot, p)) return false
        if (slot.vIndex != null && p.vIndex != null && Number(slot.vIndex) !== Number(p.vIndex)) {
          return false
        }
        return true
      }) || null
    }
    if (prev) usedPrev.add(String(prev.id))

    const next = {
      id,
      type: waSlotType(slot),
      format,
      ...(slot.vIndex != null ? { vIndex: slot.vIndex } : {}),
      ...(slot.subType ? { subType: slot.subType } : {}),
      ...(slot.buttonIndex != null ? { buttonIndex: slot.buttonIndex } : {}),
      ...emptyWaSlotValues(format),
    }

    if (prev && isCompatibleWaBinding(slot, prev)) {
      if (isMedia) {
        next.mediaLink = prev.mediaLink || ''
        next.media = prev.media || ''
        next.text = prev.mediaLink || prev.media || prev.text || ''
        if (format === 'document') {
          next.fileName = prev.fileName || ''
        }
      } else {
        next.text = prev.text || ''
      }
    }

    result[id] = next
  })

  return result
}

export function waComponentsMapsEqual(a, b) {
  const left = normalizeWaComponentsMap(a)
  const right = normalizeWaComponentsMap(b)
  const leftKeys = sortedWaComponentIds(left)
  const rightKeys = sortedWaComponentIds(right)
  if (leftKeys.length !== rightKeys.length) return false
  return leftKeys.every((id, idx) => {
    if (id !== rightKeys[idx]) return false
    const l = left[id] || {}
    const r = right[id] || {}
    return waSlotFormat(l) === waSlotFormat(r)
      && waSlotType(l) === waSlotType(r)
      && String(l.text || '') === String(r.text || '')
      && String(l.mediaLink || '') === String(r.mediaLink || '')
      && String(l.media || '') === String(r.media || '')
      && String(l.fileName || '') === String(r.fileName || '')
  })
}

/**
 * Client-side WA mapping health for list badges / dialog warnings.
 * Flags format drift (e.g. image mapping on a video template) and unfilled slots.
 */
export function getWaMappingHealth(workflow) {
  if (!workflow || !workflow.waTemplateKey || workflow.waTemplateKey === 'NONE') {
    return { broken: false, reason: null }
  }
  if (workflow._waKeyBroken === true) {
    return {
      broken: true,
      keyBroken: true,
      reason: workflow._waKeyBrokenReason || 'WhatsApp template key is broken',
    }
  }
  if (workflow._waHydrateError) {
    return { broken: true, reason: workflow._waHydrateError }
  }

  const waTemplate = workflow.waTemplate
  if (!waTemplate || typeof waTemplate !== 'object') {
    return { broken: false, reason: null }
  }

  const stored = normalizeWaComponentsMap(workflow.waComponents)
  const templateSlots = extractTemplatePlaceholders(waTemplate)
  if (templateSlots.length === 0) {
    return { broken: false, reason: null }
  }

  const storedIds = sortedWaComponentIds(stored)
  if (storedIds.length === 0) {
    return {
      broken: true,
      unfilled: true,
      reason: 'WhatsApp template variables are not mapped',
    }
  }

  for (let i = 0; i < Math.min(storedIds.length, templateSlots.length); i += 1) {
    const storedSlot = stored[storedIds[i]]
    const expected = templateSlots[i]
    if (waSlotFormat(storedSlot) !== waSlotFormat(expected)) {
      return {
        broken: true,
        formatMismatch: true,
        reason: 'Variable mapping needs to be reset',
      }
    }
    if (waSlotType(expected) && waSlotType(storedSlot) && waSlotType(expected) !== waSlotType(storedSlot)) {
      return {
        broken: true,
        formatMismatch: true,
        reason: 'Variable mapping needs to be reset',
      }
    }
  }

  if (storedIds.length !== templateSlots.length) {
    return {
      broken: true,
      formatMismatch: true,
      reason: 'Variable mapping needs to be reset',
    }
  }

  const remapped = remapWaComponentsToTemplate(waTemplate, stored, stored)
  const slots = getWaMappingSlots(remapped, waTemplate)
  if (!areAllWaMappingSlotsFilled(slots, remapped)) {
    return {
      broken: true,
      unfilled: true,
      reason: 'WhatsApp template variables are not fully mapped',
    }
  }

  return { broken: false, reason: null }
}

/**
 * Builds waComponents keyed object for PUT, preserving BE slot ids/shape.
 */
export function buildWaComponentsFromBindings(slots, bindingsMap) {
  const result = {}
  ;(Array.isArray(slots) ? slots : []).forEach((slot) => {
    if (!slot || slot.id == null) return
    const id = String(slot.id)
    const binding = (bindingsMap && bindingsMap[id]) || {}
    const format = String(slot.format || binding.format || 'text').toLowerCase()
    const isMedia = ['image', 'video', 'document'].includes(format)
    const next = {
      id,
      type: String(slot.type || binding.type || '').toLowerCase(),
      format,
      ...(slot.subType || binding.subType ? { subType: slot.subType || binding.subType } : {}),
      ...(slot.vIndex != null || binding.vIndex != null
        ? { vIndex: slot.vIndex != null ? slot.vIndex : binding.vIndex }
        : {}),
      ...(slot.buttonIndex != null || binding.buttonIndex != null
        ? { buttonIndex: slot.buttonIndex != null ? slot.buttonIndex : binding.buttonIndex }
        : {}),
      text: binding.text != null ? binding.text : (slot.text || ''),
    }
    if (isMedia) {
      next.mediaLink = binding.mediaLink || slot.mediaLink || ''
      next.media = binding.media || slot.media || ''
      // Legacy rows sometimes only stored the public URL on `media`
      if (!next.mediaLink && next.media && /^https?:\/\//i.test(String(next.media))) {
        next.mediaLink = next.media
      }
      if (binding.fileName || slot.fileName) {
        next.fileName = binding.fileName || slot.fileName || ''
      }
    }
    result[id] = next
  })
  return result
}

/**
 * Helpers for base route resolution
 */
export function getWorkflowsRoute(orgId) {
  return orgId ? `/admin/orgs/${orgId}/workflows` : '/workflows'
}

export function getWorkflowDetailRoute(orgId, workflowId) {
  const base = getWorkflowsRoute(orgId)
  return `${base}/${workflowId}`
}

/**
 * Standard Custom Fields automatically provisioned across all tenants
 */
export const STANDARD_CUSTOM_FIELDS = [
  {
    key: 'followupDatetime',
    label: 'Next Followup Date/Time',
    group: 'Standard Fields',
    type: 'DATETIME',
    description: 'Scheduled date and time for next lead follow-up',
  },
  {
    key: 'appointmentDatetime',
    label: 'Appointment Date/Time',
    group: 'Standard Fields',
    type: 'DATETIME',
    description: 'Scheduled date and time for lead consultation or appointment',
  },
  {
    key: 'appointmentService',
    label: 'Service',
    group: 'Standard Fields',
    type: 'SELECT_MULTI',
    description: 'Requested or discussed service/product category',
  },
]

export const STANDARD_CUSTOM_FIELD_KEYS = STANDARD_CUSTOM_FIELDS.map(f => f.key)

/**
 * Auto-inserted Input Fields keyed by Next Contact Status.
 * These are managed by the UI (not selectable in the create/edit dialog).
 */
export const STATUS_OPTION_FIELD_MAP = {
  'ATTEMPTED TO CONTACT': 'OPTIONS_ATC',
  UNQUALIFIED: 'OPTIONS_UNQUALIFICATION',
  LOST: 'OPTIONS_LOST',
  'BAD TIMING': 'OPTIONS_BADTIMING',
}

export const STATUS_OPTION_FIELD_KEYS = Object.values(STATUS_OPTION_FIELD_MAP)

export const STATUS_OPTION_FIELD_LABELS = {
  OPTIONS_ATC: 'Contact Failure Reason',
  OPTIONS_UNQUALIFICATION: 'Unqualification Reason',
  OPTIONS_LOST: 'Lost Reason',
  OPTIONS_BADTIMING: 'Bad Timing Reason',
}

export function getOptionFieldForStatus(nextLeadStatus) {
  if (!nextLeadStatus || nextLeadStatus === 'NONE') return null
  return STATUS_OPTION_FIELD_MAP[nextLeadStatus] || null
}

export function isStatusOptionField(fieldKey) {
  return Boolean(fieldKey && STATUS_OPTION_FIELD_KEYS.includes(fieldKey))
}

/**
 * Removes auto-managed status option fields (for dialog display / dropdown).
 */
export function stripStatusOptionFields(fieldsToUpdate) {
  if (!Array.isArray(fieldsToUpdate)) return []
  return fieldsToUpdate.filter(key => !isStatusOptionField(key))
}

/**
 * Ensures fieldsToUpdate contains the correct OPTIONS_* field for nextLeadStatus,
 * overriding any recipe/legacy/manual OPTIONS_* entries.
 */
export function applyStatusOptionField(fieldsToUpdate, nextLeadStatus) {
  const withoutOptions = stripStatusOptionFields(fieldsToUpdate)
  const optionField = getOptionFieldForStatus(nextLeadStatus)
  if (!optionField) return withoutOptions
  return [optionField, ...withoutOptions]
}

/**
 * Converts a field key to a human-readable display label
 */
export function formatFieldDisplayName(fieldKey) {
  if (!fieldKey) return ''
  if (STATUS_OPTION_FIELD_LABELS[fieldKey]) return STATUS_OPTION_FIELD_LABELS[fieldKey]
  const standard = STANDARD_CUSTOM_FIELDS.find(f => f.key === fieldKey)
  if (standard) return standard.label

  return String(fieldKey)
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim()
}

/**
 * Returns options for fieldsToUpdate directly from fieldDef without injecting synthetic standard fields.
 * Status-managed OPTIONS_* keys are excluded (auto-inserted from nextLeadStatus instead).
 */
export function getCustomFieldOptionsWithStandard(fieldDef) {
  const rawOptions = getFieldOptions(fieldDef, 'fieldsToUpdate')
  return rawOptions
    .map(opt => {
      const key = typeof opt === 'object' ? (opt.key || opt.value) : opt
      const label = typeof opt === 'object' ? (opt.label || opt.text || opt.key || opt.value) : String(opt)
      return {
        key,
        value: key,
        label,
      }
    })
    .filter(opt => !isStatusOptionField(opt.key))
}

/**
 * Route to WhatsApp / CRM Numbers settings for the given org
 */
export function getCrmNumbersRoute(orgId) {
  return orgId ? `/admin/orgs/${orgId}/crm-numbers` : '/crm-numbers'
}

export function getWhatsAppSettingsRoute(orgId) {
  return getCrmNumbersRoute(orgId)
}

/**
 * Standard Business Categories for Workflow Recipes
 */
export const WORKFLOW_RECIPE_CATEGORIES = [
  {
    key: 'lead_engagement',
    label: 'Lead Engagement & Outreach',
    description: 'Workflows for initiating contact, logging call attempts, and following up with leads.',
  },
  {
    key: 'qualification_pipeline',
    label: 'Qualification & Pipeline',
    description: 'Workflows for moving leads through pipeline stages from qualification to disqualification.',
  },
  {
    key: 'appointments_meetings',
    label: 'Appointments & Meetings',
    description: 'Workflows for booking consultations, meetings, reminders, and managing attendance.',
  },
  {
    key: 'sales_conversion',
    label: 'Sales & Conversion',
    description: 'Workflows for closing deals, logging payments, and converting contacts into customers.',
  },
  {
    key: 'administrative_ops',
    label: 'Administrative & Operations',
    description: 'General task workflows for scheduling follow-ups, logging interaction notes, and tagging.',
  },
]

/**
 * Extracts options for a specific field key from fieldDef
 */
export function getFieldOptions(fieldDef, fieldKey) {
  if (!fieldDef || typeof fieldDef !== 'object') return []

  // Check fieldDef.options[fieldKey]
  if (fieldDef.options && Array.isArray(fieldDef.options[fieldKey])) {
    return fieldDef.options[fieldKey]
  }

  // Check fieldDef.steps
  if (Array.isArray(fieldDef.steps)) {
    for (const step of fieldDef.steps) {
      if (step && Array.isArray(step.fields)) {
        const f = step.fields.find(item => item && item.key === fieldKey)
        if (f && Array.isArray(f.options)) {
          return f.options
        }
      }
    }
  }

  // Check fieldDef[fieldKey]
  if (fieldDef[fieldKey] && Array.isArray(fieldDef[fieldKey].options)) {
    return fieldDef[fieldKey].options
  }

  return []
}

/**
 * Tag keys from workflow fieldDef (tagsToAdd / tagsToRemove options).
 * Used to avoid a separate contact-attrs GET when pb=fieldDef already includes them.
 */
export function getTagKeysFromFieldDef(fieldDef) {
  const keys = new Set()
  for (const opt of [
    ...getFieldOptions(fieldDef, 'tagsToAdd'),
    ...getFieldOptions(fieldDef, 'tagsToRemove'),
  ]) {
    const key = (opt && typeof opt === 'object')
      ? (opt.key || opt.label || opt.text)
      : opt
    if (key != null && String(key).trim()) keys.add(String(key).trim())
  }
  return Array.from(keys)
}

/**
 * Constructs a standard formDefinition for DynamicFormDialog (used for both Add and Edit of a Workflow).
 *
 * @param {Object} params
 * @param {Object} [params.fieldDef] - Raw fieldDef from GET /workflows?pb=fieldDef
 * @param {Array} [params.availableTags] - Available tags array from backend
 * @param {string} [params.mode] - 'add' | 'edit'
 * @param {Object} [params.prefill] - Existing workflow or recipe prefill object
 * @returns {Object} formDefinition for DynamicFormDialog
 */
export function getWorkflowFormDefinition({
  fieldDef = {},
  availableTags = [],
  mode = 'add',
  prefill = {},
  subOrgs = [],
  orgId = '',
} = {}) {
  const subOrgOptions = getSubOrgOptions(fieldDef)
  const hasTeams = tenantHasWorkflowTeams(fieldDef, subOrgs)

  const rawNextLeadStatus = getFieldOptions(fieldDef, 'nextLeadStatus')
  const nextLeadStatusOptions = rawNextLeadStatus
    .map(opt => ({
      key: typeof opt === 'object' ? (opt.key || opt.value) : opt,
      label: typeof opt === 'object' ? (opt.label || opt.text || opt.key || opt.value) : String(opt),
    }))
    .filter(opt => opt && opt.key && opt.key !== 'NONE' && opt.key !== 'none')

  const allTagKeys = new Set([
    ...availableTags.map(t => (typeof t === 'object' ? (t.key || t.label || t.text) : t)),
    ...getFieldOptions(fieldDef, 'tags').map(t => (typeof t === 'object' ? (t.key || t.label || t.text) : t)),
    ...getFieldOptions(fieldDef, 'tagsToAdd').map(t => (typeof t === 'object' ? (t.key || t.label || t.text) : t)),
    ...getFieldOptions(fieldDef, 'tagsToRemove').map(t => (typeof t === 'object' ? (t.key || t.label || t.text) : t)),
    ...(Array.isArray(prefill.tagsToAdd) ? prefill.tagsToAdd : []),
    ...(Array.isArray(prefill.tagsToRemove) ? prefill.tagsToRemove : []),
  ])
  const tagOptions = Array.from(allTagKeys).filter(Boolean).map(t => ({ key: t, label: t }))

  const rawFieldsToUpdate = getFieldOptions(fieldDef, 'fieldsToUpdate')
  const fieldsToUpdateOptions = rawFieldsToUpdate
    .map(opt => ({
      key: typeof opt === 'object' ? (opt.key || opt.value) : opt,
      label: typeof opt === 'object' ? (opt.label || opt.text || opt.key || opt.value) : String(opt),
    }))
    .filter(opt => !isStatusOptionField(opt.key))
  const rawFieldsToClear = getFieldOptions(fieldDef, 'fieldsToClear')
  const fieldsToClearOptions = rawFieldsToClear.map(opt => ({
    key: typeof opt === 'object' ? (opt.key || opt.value) : opt,
    label: typeof opt === 'object' ? (opt.label || opt.text || opt.key || opt.value) : String(opt),
  }))

  // Dialog hides auto-managed OPTIONS_*; they are re-applied on submit from nextLeadStatus
  const dialogFieldsToUpdate = stripStatusOptionFields(
    Array.isArray(prefill.fieldsToUpdate) ? prefill.fieldsToUpdate : []
  )

  // Ensure any prefill nextLeadStatus is present
  if (prefill.nextLeadStatus && !nextLeadStatusOptions.some(o => o.key === prefill.nextLeadStatus)) {
    nextLeadStatusOptions.unshift({
      key: prefill.nextLeadStatus,
      label: prefill.nextLeadStatus,
    })
  }

  // Ensure any prefill fieldsToUpdate are present (excluding status OPTIONS_*)
  dialogFieldsToUpdate.forEach(key => {
    if (key && !fieldsToUpdateOptions.some(o => o.key === key)) {
      fieldsToUpdateOptions.push({
        key,
        label: formatFieldDisplayName(key),
      })
    }
  })

  // Ensure any prefill fieldsToClear are present
  if (Array.isArray(prefill.fieldsToClear)) {
    prefill.fieldsToClear.forEach(key => {
      if (key && !fieldsToClearOptions.some(o => o.key === key)) {
        fieldsToClearOptions.push({
          key,
          label: formatFieldDisplayName(key),
        })
      }
    })
  }

  const fields = [
    {
      key: 'title',
      label: 'Workflow Title',
      type: 'edit_text',
      required: true,
      v_required: { value: true, err: 'Workflow Title is required' },
      placeholder: 'e.g. QUALIFY LEAD, ATC CALL',
      tip: 'Button label staff see on contact cards. Must be uppercase letters, digits, spaces, parentheses, underscores.',
      value: prefill.title || '',
      defaultValue: prefill.title || '',
      placement: 'left',
    },
    {
      key: 'nextLeadStatus',
      label: 'Next Contact Status',
      type: 'select',
      required: false,
      v_required: { value: false, err: '' },
      options: nextLeadStatusOptions,
      value: prefill.nextLeadStatus !== undefined ? prefill.nextLeadStatus : '',
      defaultValue: prefill.nextLeadStatus !== undefined ? prefill.nextLeadStatus : '',
      tip: 'Transition the contact to this lead status upon workflow execution.',
      placement: 'right',
    },
    {
      key: 'tagsToAdd',
      label: 'Tags to Add',
      type: 'select',
      multiSelect: true,
      v_required: { value: false, err: '' },
      options: tagOptions,
      value: Array.isArray(prefill.tagsToAdd) ? prefill.tagsToAdd : [],
      defaultValue: Array.isArray(prefill.tagsToAdd) ? prefill.tagsToAdd : [],
      tip: 'Tags added to the contact upon running this workflow.',
      placement: 'left',
    },
    {
      key: 'tagsToRemove',
      label: 'Tags to Remove',
      type: 'select',
      multiSelect: true,
      v_required: { value: false, err: '' },
      options: tagOptions,
      value: Array.isArray(prefill.tagsToRemove) ? prefill.tagsToRemove : [],
      defaultValue: Array.isArray(prefill.tagsToRemove) ? prefill.tagsToRemove : [],
      tip: 'Tags removed from the contact upon running this workflow.',
      placement: 'right',
    },
    {
      key: 'fieldsToUpdate',
      label: 'Input Fields',
      type: 'select',
      multiSelect: true,
      v_required: { value: false, err: '' },
      options: fieldsToUpdateOptions.map(o => ({
        key: o.key,
        label: o.label,
      })),
      value: dialogFieldsToUpdate,
      defaultValue: dialogFieldsToUpdate,
      tip: 'Standard & custom fields staff will be prompted to fill in when running the workflow.',
      placement: 'left',
    },
    {
      key: 'fieldsToClear',
      label: 'Fields to Reset / Clear',
      type: 'select',
      multiSelect: true,
      v_required: { value: false, err: '' },
      options: fieldsToClearOptions,
      value: Array.isArray(prefill.fieldsToClear) ? prefill.fieldsToClear : [],
      defaultValue: Array.isArray(prefill.fieldsToClear) ? prefill.fieldsToClear : [],
      tip: 'Fields cleared/reset on contact execution.',
      placement: 'right',
    },
  ]

  // "Enabled For Teams" only appears if there is at least 1 team in the tenant; moved to advanced
  if (hasTeams) {
    const defaultSubOrgSelection = getDefaultEnabledSubOrgIds(subOrgOptions, orgId)
    const initialEnabledSubOrgIds = (mode === 'add')
      ? (Array.isArray(prefill.enabledSubOrgIds) && prefill.enabledSubOrgIds.length > 0
          ? prefill.enabledSubOrgIds
          : defaultSubOrgSelection)
      : (Array.isArray(prefill.enabledSubOrgIds) ? prefill.enabledSubOrgIds : [])

    fields.push({
      key: 'enabledSubOrgIds',
      label: 'Enabled For Teams',
      type: 'select',
      multiSelect: true,
      advanced: true,
      v_required: { value: false, err: '' },
      options: subOrgOptions.map(o => ({
        key: typeof o === 'object' ? (o.key != null ? String(o.key) : String(o.value)) : String(o),
        label: typeof o === 'object' ? (o.label || o.text || o.key || o.value) : String(o),
      })),
      value: initialEnabledSubOrgIds,
      defaultValue: initialEnabledSubOrgIds,
      tip: 'Select "All Teams" or specific sub-teams where this workflow is available.',
      placement: 'left',
    })
  }

  fields.push(
    {
      key: 'captureNote',
      label: 'Capture Note',
      type: 'select',
      advanced: true,
      v_required: { value: false, err: '' },
      value: prefill.captureNote === 'ENABLED' ? 'ENABLED' : 'DISABLED',
      defaultValue: prefill.captureNote === 'ENABLED' ? 'ENABLED' : 'DISABLED',
      options: [
        { key: 'DISABLED', label: 'DISABLED' },
        { key: 'ENABLED', label: 'ENABLED' },
      ],
      tip: 'Prompt staff to record a note when running this workflow.',
      placement: hasTeams ? 'right' : 'left',
    },
    {
      key: 'paymentTransaction',
      label: 'Payment Transaction',
      type: 'select',
      advanced: true,
      v_required: { value: false, err: '' },
      value: prefill.paymentTransaction === 'ENABLED' ? 'ENABLED' : 'DISABLED',
      defaultValue: prefill.paymentTransaction === 'ENABLED' ? 'ENABLED' : 'DISABLED',
      options: [
        { key: 'DISABLED', label: 'DISABLED' },
        { key: 'ENABLED', label: 'ENABLED' },
      ],
      tip: 'Collect payment transaction when running this workflow.',
      placement: hasTeams ? 'left' : 'right',
    },
    {
      key: 'workflowKey',
      label: 'Workflow Key (Optional)',
      type: 'edit_text',
      advanced: true,
      v_required: { value: false, err: '' },
      placeholder: 'e.g. qualifyLead',
      value: prefill.workflowKey || '',
      defaultValue: prefill.workflowKey || '',
      tip: 'Stable identifier. Leave blank to auto-generate from title.',
      placement: hasTeams ? 'right' : 'left',
    }
  )

  if (prefill.legacyWorkflowKey) {
    fields.push({
      key: 'legacyWorkflowKey',
      type: 'hidden',
      value: prefill.legacyWorkflowKey,
      defaultValue: prefill.legacyWorkflowKey,
    })
  }

  return {
    title: mode === 'edit' ? 'Edit Workflow' : 'Create Workflow',
    formType: mode === 'edit' ? 'edit' : 'add',
    steps: [
      {
        title: 'Workflow Details',
        fields,
      },
    ],
  }
}

/**
 * Status config for legacy workflow migration statuses.
 * Only two states: MAPPED (when workflow with similar key is live) or UNMAPPED.
 */
export const LEGACY_STATUS_CONFIG = {
  UNMAPPED: {
    label: 'Unmapped',
    color: 'warning',
    bg: '#FEF3C7',
    text: '#92400E',
    border: '#FCD34D',
  },
  MAPPED: {
    label: 'Mapped',
    color: 'success',
    bg: '#ECFDF5',
    text: '#065F46',
    border: '#A7F3D0',
  },
}

/**
 * Returns metadata for displaying a legacy workflow status badge.
 */
export function getLegacyStatusMeta(status) {
  if (status === 'MAPPED' || status === 'MAPPED_LIVE') {
    return LEGACY_STATUS_CONFIG.MAPPED
  }
  return LEGACY_STATUS_CONFIG.UNMAPPED
}

/**
 * Maps legacy enabledContexts (e.g. ["MAIN_ORG", "subOrgId1"]) into Dynamo enabledSubOrgIds.
 * Legacy MAIN_ORG is a sentinel for parent workflowsEnabled — rewrite to real orgId.
 */
export function mapLegacyContextsToSubOrgIds(enabledContexts, subOrgOptions = [], orgId = '') {
  if (!Array.isArray(enabledContexts) || enabledContexts.length === 0) {
    return []
  }

  const mainOrgId = getMainOrgId(subOrgOptions, orgId)
  const rewriteContextId = (id) => {
    if (id == null || id === '') return null
    if (id === 'MAIN_ORG' || id === 'Main Org') return mainOrgId || null
    if (id === 'ALL_ORGS' || id === 'All Teams') return ALL_SUBORGS_KEY
    return String(id)
  }

  const hasAllTeams = enabledContexts.some(id => (
    id === ALL_SUBORGS_KEY || id === 'ALL_ORGS' || id === 'All Teams'
  ))
  const hasMain = enabledContexts.some(id => (
    id === 'MAIN_ORG' || id === 'Main Org' || (mainOrgId && String(id) === String(mainOrgId))
  ))

  if (hasAllTeams) {
    const result = [ALL_SUBORGS_KEY]
    if (hasMain && mainOrgId) result.push(String(mainOrgId))
    return sanitizeEnabledSubOrgIdsForOrg(result, subOrgOptions, orgId)
  }

  const validTeamOptions = subOrgOptions.filter(o => {
    const key = o && (o.key != null ? o.key : o.value)
    return key && key !== ALL_SUBORGS_KEY && key !== 'ALL_ORGS' && key !== 'MAIN_ORG'
      && !(mainOrgId && String(key) === String(mainOrgId))
  })
  const allowedTeamKeys = new Set(
    validTeamOptions.map((o) => String(o.key != null ? o.key : o.value))
  )
  const teamContextIds = enabledContexts
    .map(rewriteContextId)
    .filter(id => id && id !== ALL_SUBORGS_KEY && !(mainOrgId && id === String(mainOrgId)))
    // Ignore foreign team ids so they do not inflate "all teams selected"
    .filter(id => allowedTeamKeys.size === 0 || allowedTeamKeys.has(String(id)))

  // All real teams selected → collapse to ALL_SUBORGS (+ main if enabled)
  if (validTeamOptions.length > 0 && teamContextIds.length >= validTeamOptions.length) {
    const result = [ALL_SUBORGS_KEY]
    if (hasMain && mainOrgId) result.push(String(mainOrgId))
    return sanitizeEnabledSubOrgIdsForOrg(result, subOrgOptions, orgId)
  }

  return sanitizeEnabledSubOrgIdsForOrg(
    cleanEnabledSubOrgIds(enabledContexts.map(rewriteContextId).filter(Boolean), mainOrgId),
    subOrgOptions,
    orgId
  )
}

/**
 * Renders badges for enabled contexts of a legacy workflow item.
 */
export function renderLegacyContextBadges(enabledContexts, subOrgOptions = []) {
  if (!Array.isArray(enabledContexts) || enabledContexts.length === 0) {
    return (
      <Typography variant='caption' color='textSecondary' style={{ fontStyle: 'italic' }}>
        None
      </Typography>
    )
  }

  if (enabledContexts.includes(ALL_SUBORGS_KEY) || enabledContexts.includes('ALL_ORGS') || enabledContexts.includes('All Teams')) {
    return (
      <Chip
        size='small'
        variant='outlined'
        label='All Teams'
        style={{ fontSize: 11, height: 22 }}
      />
    )
  }

  const optionMap = {
    MAIN_ORG: 'Main Org',
  }
  subOrgOptions.forEach(opt => {
    if (opt && opt.key != null) {
      optionMap[opt.key] = opt.label || opt.text || opt.key
    }
  })

  if (enabledContexts.length <= 2) {
    return (
      <Box display='flex' flexWrap='wrap' style={{ gap: 4 }}>
        {enabledContexts.map(id => (
          <Chip
            key={id}
            size='small'
            variant='outlined'
            label={optionMap[id] || id}
            style={{ fontSize: 11, height: 22 }}
          />
        ))}
      </Box>
    )
  }

  const firstId = enabledContexts[0]
  const remainingCount = enabledContexts.length - 1
  const allNames = enabledContexts.map(id => optionMap[id] || id)

  return (
    <Box display='flex' alignItems='center' style={{ gap: 4 }}>
      <Chip
        size='small'
        variant='outlined'
        label={optionMap[firstId] || firstId}
        style={{ fontSize: 11, height: 22 }}
      />
      <Tooltip title={allNames.join(', ')} placement='top'>
        <Chip
          size='small'
          label={`+${remainingCount}`}
          style={{ fontSize: 11, height: 22, backgroundColor: '#E5E7EB', cursor: 'pointer' }}
        />
      </Tooltip>
    </Box>
  )
}

/**
 * Constructs the payload to create a new DynamoDB workflow from a legacy workflow item.
 * Supports prefilling from recipe if available.
 * Prefer legacy-inferred captureNote / paymentTransaction / fieldsToUpdate when present
 * so migrate does not inherit incorrect recipe defaults.
 */
export function buildWorkflowPayloadFromLegacy({
  legacyItem,
  recipe = null,
  fieldDef = {},
  orgId = '',
} = {}) {
  if (!legacyItem || typeof legacyItem !== 'object') return null

  const rawTitle = legacyItem.title || (recipe && recipe.title) || legacyItem.key || ''
  const title = sanitizeWorkflowTitle(rawTitle)

  // Always prefer the legacy workflow key (not the recipe key) so migration preserves the existing key
  const rawKey = legacyItem.key || ''
  const workflowKey = isValidWorkflowKey(rawKey) ? rawKey : undefined

  const subOrgOptions = getSubOrgOptions(fieldDef)
  const enabledSubOrgIds = sanitizeEnabledSubOrgIdsForOrg(
    mapLegacyContextsToSubOrgIds(
      legacyItem.enabledContexts,
      subOrgOptions,
      orgId
    ),
    subOrgOptions,
    orgId
  )

  const inferred = (legacyItem.inferredConfig && typeof legacyItem.inferredConfig === 'object')
    ? legacyItem.inferredConfig
    : null

  const payload = {
    title,
    state: 'DISABLED',
    enabledSubOrgIds,
    legacyWorkflowKey: legacyItem.key,
  }

  if (workflowKey) {
    payload.workflowKey = workflowKey
  }

  if (recipe) {
    if (recipe.nextLeadStatus && recipe.nextLeadStatus !== 'NONE') {
      payload.nextLeadStatus = recipe.nextLeadStatus
    }
    if (Array.isArray(recipe.tagsToAdd) && recipe.tagsToAdd.length > 0) {
      payload.tagsToAdd = recipe.tagsToAdd
    }
    if (Array.isArray(recipe.tagsToRemove) && recipe.tagsToRemove.length > 0) {
      payload.tagsToRemove = recipe.tagsToRemove
    }
    if (Array.isArray(recipe.fieldsToUpdate) && recipe.fieldsToUpdate.length > 0) {
      payload.fieldsToUpdate = recipe.fieldsToUpdate
    }
    if (Array.isArray(recipe.fieldsToClear) && recipe.fieldsToClear.length > 0) {
      payload.fieldsToClear = recipe.fieldsToClear
    }
    if (recipe.waTemplateKey && recipe.waTemplateKey !== 'NONE') {
      payload.waTemplateKey = recipe.waTemplateKey
    }
    payload.captureNote = recipe.captureNote === 'ENABLED' ? 'ENABLED' : 'DISABLED'
    payload.paymentTransaction = recipe.paymentTransaction === 'ENABLED' ? 'ENABLED' : 'DISABLED'
  } else {
    payload.captureNote = 'DISABLED'
    payload.paymentTransaction = 'DISABLED'
  }

  // Legacy-inferred flags win (reference org workflows rarely capture notes; payment must stay on).
  if (inferred) {
    if (inferred.captureNote === 'ENABLED' || inferred.captureNote === 'DISABLED') {
      payload.captureNote = inferred.captureNote
    }
    if (inferred.paymentTransaction === 'ENABLED' || inferred.paymentTransaction === 'DISABLED') {
      payload.paymentTransaction = inferred.paymentTransaction
    }
    if (Array.isArray(inferred.fieldsToUpdate)) {
      payload.fieldsToUpdate = inferred.fieldsToUpdate
    }
  }

  // Status-managed OPTIONS_* always win over recipe / legacy inferred fieldsToUpdate
  const hadFieldsToUpdate = Array.isArray(payload.fieldsToUpdate)
  payload.fieldsToUpdate = applyStatusOptionField(
    payload.fieldsToUpdate,
    payload.nextLeadStatus
  )
  if (payload.fieldsToUpdate.length === 0 && !hadFieldsToUpdate) {
    delete payload.fieldsToUpdate
  }

  return payload
}

/**
 * Determines if a legacy workflow is eligible for migration:
 * 1. Must belong to CRMCONTACT entity (entities includes 'CRMCONTACT').
 * 2. Must not be triggered by event listeners (FORM_SUBMITTED, WHATSAPP_INBOUND, CONTACT_ENGAGEMENT, etc.),
 *    i.e. triggerEvent must be empty / not configured.
 * 3. Must be a staff / human-facing workflow (isStaffWorkflow !== false).
 */
export function isMigratableLegacyWorkflow(item) {
  if (!item || typeof item !== 'object') return false

  // Restrict to CRMCONTACT entity workflows only
  const hasCrmContact = Array.isArray(item.entities) && item.entities.includes('CRMCONTACT')
  if (!hasCrmContact) return false

  // Must not be triggered by events (e.g. FORM_SUBMITTED, WHATSAPP_INBOUND, CONTACT_ENGAGEMENT, etc.)
  const hasTriggerEvent = Boolean(item.triggerEvent && String(item.triggerEvent).trim() !== '')
  if (hasTriggerEvent) return false

  // Staff workflow check
  if (item.isStaffWorkflow === false) return false

  return true
}

/**
 * Exact workflow-key equality (trim + case-insensitive).
 * Migration preserves the legacy key, so prefix/substring similarity is not used.
 */
export function areKeysSimilar(keyA, keyB) {
  if (!keyA || !keyB) return false
  return String(keyA).trim().toLowerCase() === String(keyB).trim().toLowerCase()
}

/**
 * Checks whether a DynamoDB workflow matches a legacy workflow item.
 */
export function matchesLegacyWorkflow(legacyItem, wf) {
  if (!legacyItem || !wf) return false

  // 1. Direct ID match
  if (legacyItem.mappedWorkflowId && wf.id === legacyItem.mappedWorkflowId) {
    return true
  }
  // 2. Explicit legacyWorkflowKey on workflow
  if (wf.legacyWorkflowKey && areKeysSimilar(wf.legacyWorkflowKey, legacyItem.key)) {
    return true
  }
  // 3. Similar workflow keys
  if (wf.workflowKey) {
    if (areKeysSimilar(wf.workflowKey, legacyItem.key)) return true
    if (legacyItem.mappedWorkflowKey && areKeysSimilar(wf.workflowKey, legacyItem.mappedWorkflowKey)) return true
    if (legacyItem.suggestedRecipeKey && areKeysSimilar(wf.workflowKey, legacyItem.suggestedRecipeKey)) return true
  }
  // 4. Recipe key match
  if (wf.recipeKey && legacyItem.suggestedRecipeKey && areKeysSimilar(wf.recipeKey, legacyItem.suggestedRecipeKey)) {
    return true
  }
  // 5. Identical title
  if (wf.title && legacyItem.title && String(wf.title).trim().toUpperCase() === String(legacyItem.title).trim().toUpperCase()) {
    return true
  }
  return false
}

/**
 * Finds a matching DynamoDB workflow for a legacy workflow item.
 * Prefers LIVE workflow if available; otherwise matches any existing workflow (e.g. DISABLED/draft).
 */
export function findMatchingWorkflow(legacyItem, workflows = []) {
  if (!legacyItem || !Array.isArray(workflows) || workflows.length === 0) return null

  // Prefer LIVE workflow if available
  const liveMatch = workflows.find(wf => wf && wf.state === 'LIVE' && matchesLegacyWorkflow(legacyItem, wf))
  if (liveMatch) return liveMatch

  // Otherwise, match any existing workflow (e.g. DISABLED/draft)
  return workflows.find(wf => wf && matchesLegacyWorkflow(legacyItem, wf)) || null
}

/**
 * Finds a matching DynamoDB workflow for a legacy workflow item.
 * Kept for backward compatibility.
 */
export function findMatchingLiveWorkflow(legacyItem, workflows = []) {
  return findMatchingWorkflow(legacyItem, workflows)
}

/**
 * Returns true when a Dynamo workflow replaces a legacy workflow by exact workflowKey match:
 * items[n].workflowKey === allLegacyActiveWorkflows[m].key (case-insensitive).
 */
export function isWorkflowMappedToLegacy(workflow, allLegacyActiveWorkflows = []) {
  if (!workflow || !workflow.workflowKey || !Array.isArray(allLegacyActiveWorkflows)) {
    return false
  }
  return allLegacyActiveWorkflows.some(
    legacy => legacy && areKeysSimilar(workflow.workflowKey, legacy.key)
  )
}

/**
 * Checks whether a legacy workflow is MAPPED per backend status.
 * There are only 2 states for legacy workflows: MAPPED or UNMAPPED.
 * Does not infer mapped from local Dynamo workflows (id/key/recipe/title).
 */
export function isLegacyWorkflowMapped(legacyItem) {
  if (!legacyItem || typeof legacyItem !== 'object') return false

  return legacyItem.status === 'MAPPED'
    || legacyItem.status === 'MAPPED_LIVE'
    || legacyItem.status === 'MAPPED_DRAFT'
    || Boolean(legacyItem.mappedWorkflowId)
}

/**
 * Sanitizes legacyMigration object from backend:
 * Restricts all legacy lists and counts to migratable CRMCONTACT workflows only.
 * Normalizes all legacy statuses to either 'MAPPED' or 'UNMAPPED' using backend status only.
 */
export function sanitizeLegacyMigrationData(legacyMigration) {
  if (!legacyMigration || typeof legacyMigration !== 'object') return null

  const rawList = Array.isArray(legacyMigration.allLegacyActiveWorkflows) && legacyMigration.allLegacyActiveWorkflows.length > 0
    ? legacyMigration.allLegacyActiveWorkflows
    : (Array.isArray(legacyMigration.unmappedWorkflows) ? legacyMigration.unmappedWorkflows : [])

  const migratableItems = rawList.filter(isMigratableLegacyWorkflow)

  const allLegacyActiveWorkflows = migratableItems.map(item => {
    const isMapped = isLegacyWorkflowMapped(item)

    return {
      ...item,
      status: isMapped ? 'MAPPED' : 'UNMAPPED',
      mappedWorkflowId: isMapped ? (item.mappedWorkflowId || null) : null,
      mappedWorkflowKey: isMapped ? (item.mappedWorkflowKey || null) : null,
    }
  })

  const unmappedWorkflows = allLegacyActiveWorkflows.filter(item => item.status === 'UNMAPPED')
  const mappedWorkflows = allLegacyActiveWorkflows.filter(item => item.status === 'MAPPED')

  const unmappedCount = unmappedWorkflows.length
  const mappedCount = mappedWorkflows.length

  return {
    ...legacyMigration,
    allLegacyActiveWorkflows,
    unmappedWorkflows,
    totalLegacyActive: allLegacyActiveWorkflows.length,
    unmappedCount,
    mappedCount,
    hasUnmappedLegacyWorkflows: unmappedCount > 0,
  }
}

/**
 * Checks if there are any legacy workflows that are unmapped.
 * Since workflows are only MAPPED when live, any non-live workflow is UNMAPPED.
 */
export function hasPendingOrDisabledLegacyWorkflows(legacyMigration) {
  if (!legacyMigration || typeof legacyMigration !== 'object') {
    return false
  }

  if (typeof legacyMigration.unmappedCount === 'number') {
    return legacyMigration.unmappedCount > 0
  }

  const unmapped = Array.isArray(legacyMigration.unmappedWorkflows)
    ? legacyMigration.unmappedWorkflows.filter(isMigratableLegacyWorkflow)
    : []
  if (unmapped.some(item => item && item.status === 'UNMAPPED')) return true

  const allActive = Array.isArray(legacyMigration.allLegacyActiveWorkflows)
    ? legacyMigration.allLegacyActiveWorkflows.filter(isMigratableLegacyWorkflow)
    : []

  return allActive.some(item => item && item.status === 'UNMAPPED')
}

/**
 * Filters a list of LegacyWorkflowItem objects based on search query and filter selection.
 */
export function filterLegacyWorkflows(legacyItems, searchText = '', filter = 'UNMAPPED') {
  let list = Array.isArray(legacyItems) ? legacyItems.filter(isMigratableLegacyWorkflow) : []

  if (filter === 'UNMAPPED') {
    list = list.filter(item => item && item.status === 'UNMAPPED')
  } else if (filter === 'MAPPED') {
    list = list.filter(item => item && item.status === 'MAPPED')
  } else if (filter === 'RECIPE_READY') {
    list = list.filter(item => item && item.canCreateFromRecipe)
  }

  if (searchText && searchText.trim()) {
    const q = searchText.trim().toLowerCase()
    list = list.filter(item => {
      if (!item) return false
      const titleMatch = String(item.title || '').toLowerCase().includes(q)
      const keyMatch = String(item.key || '').toLowerCase().includes(q)
      const recipeMatch = String(item.suggestedRecipeKey || '').toLowerCase().includes(q)
      return titleMatch || keyMatch || recipeMatch
    })
  }

  return list
}

/** Missing / non-finite order sorts last (matches staff _order fallback). */
export const WORKFLOW_ORDER_MISSING = 9999
/** Legacy mapped order fallback (lexical). */
export const LEGACY_MAPPED_ORDER_DEFAULT = '999'

export function getWorkflowOrderValue(workflow) {
  const n = Number(workflow && workflow.order)
  return Number.isFinite(n) ? n : WORKFLOW_ORDER_MISSING
}

export function sortWorkflowsByOrder(workflows) {
  const list = Array.isArray(workflows) ? [...workflows] : []
  list.sort((a, b) => getWorkflowOrderValue(a) - getWorkflowOrderValue(b))
  return list
}

export function getWorkflowReorderId(workflow) {
  if (!workflow) return null
  return workflow.id || workflow._workflowId || null
}

/** Legacy order is a string; missing → '999'; compare lexically. */
export function getLegacyOrderSortKey(legacyItem) {
  if (!legacyItem || legacyItem.order == null || legacyItem.order === '') {
    return LEGACY_MAPPED_ORDER_DEFAULT
  }
  return String(legacyItem.order)
}

export function findMatchingLegacyForWorkflow(workflow, allLegacyActiveWorkflows = []) {
  if (!workflow || !workflow.workflowKey || !Array.isArray(allLegacyActiveWorkflows)) {
    return null
  }
  return allLegacyActiveWorkflows.find(
    (legacy) => legacy && areKeysSimilar(workflow.workflowKey, legacy.key)
  ) || null
}

/**
 * Admin list sort:
 * 1. Legacy-mapped Dynamo workflows first (by matching legacy.order, default '999', lexical asc)
 * 2. Then unmapped Dynamo workflows (by numeric Dynamo order, missing → 9999)
 */
export function sortWorkflowsForAdminList(workflows, allLegacyActiveWorkflows = []) {
  const list = Array.isArray(workflows) ? [...workflows] : []
  const legacyList = Array.isArray(allLegacyActiveWorkflows) ? allLegacyActiveWorkflows : []

  list.sort((a, b) => {
    const legacyA = findMatchingLegacyForWorkflow(a, legacyList)
    const legacyB = findMatchingLegacyForWorkflow(b, legacyList)
    const mappedA = Boolean(legacyA)
    const mappedB = Boolean(legacyB)

    if (mappedA !== mappedB) {
      return mappedA ? -1 : 1
    }

    if (mappedA && mappedB) {
      const orderA = getLegacyOrderSortKey(legacyA)
      const orderB = getLegacyOrderSortKey(legacyB)
      if (orderA < orderB) return -1
      if (orderA > orderB) return 1
      return 0
    }

    return getWorkflowOrderValue(a) - getWorkflowOrderValue(b)
  })

  return list
}

/**
 * Annotate mapped rows (no interactive order) and assign contiguous 1-based order
 * only to unmapped rows so TableComponent ↑/↓ controls render for those only.
 */
export function prepareWorkflowsForOrderUi(workflows, allLegacyActiveWorkflows = []) {
  const legacyList = Array.isArray(allLegacyActiveWorkflows) ? allLegacyActiveWorkflows : []
  let unmappedIndex = 0

  return (Array.isArray(workflows) ? workflows : []).map((wf) => {
    const legacy = findMatchingLegacyForWorkflow(wf, legacyList)
    if (legacy) {
      return {
        ...wf,
        _isMappedToLegacy: true,
        _legacyOrderDisplay: getLegacyOrderSortKey(legacy),
        // Falsy order → TableComponent renders Order cell without ↑/↓
        order: null,
      }
    }
    unmappedIndex += 1
    return {
      ...wf,
      _isMappedToLegacy: false,
      _legacyOrderDisplay: null,
      order: unmappedIndex,
    }
  })
}

/** @deprecated Prefer prepareWorkflowsForOrderUi — kept for simple contiguous display. */
export function applyWorkflowDisplayOrders(workflows) {
  if (!Array.isArray(workflows)) return []
  return workflows.map((wf, index) => ({
    ...wf,
    order: index + 1,
  }))
}


